/**
 * Place search box for the Chart Plotter.
 * Geocodes names, postal codes, and raw "lat, lon" via the backend
 * Nominatim proxy (/api/geo/search), then flies the map to the result.
 */
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import api from '../../config/api';

const SearchWrap = styled.div`
  position: absolute;
  top: 10px;
  left: 56px;
  z-index: 1000;
  width: min(340px, calc(100% - 120px));
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: white;
  border-radius: 10px;
  padding: 0.5rem 0.75rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);

  /* Explicit colors required: the app's dark-theme global styles set input
     text to near-white, which is invisible on this white box. */
  input {
    flex: 1;
    border: none;
    outline: none;
    font-size: 0.9rem;
    background: transparent;
    color: #111827;
    caret-color: #111827;

    &::placeholder {
      color: #9ca3af;
    }

    &:focus {
      outline: none;
      border: none;
      box-shadow: none;
    }
  }

  svg {
    width: 18px;
    height: 18px;
    color: #6b7280;
    flex-shrink: 0;
  }

  button {
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    display: flex;
  }
`;

const Results = styled.div`
  margin-top: 6px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  max-height: 320px;
  overflow-y: auto;
`;

const ResultItem = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  border-bottom: 1px solid #f3f4f6;
  padding: 0.6rem 0.85rem;
  cursor: pointer;
  font-size: 0.85rem;
  color: #111827;

  &:hover { background: #f0f9ff; }
  &:last-child { border-bottom: none; }

  .type {
    color: #6b7280;
    font-size: 0.72rem;
    text-transform: capitalize;
  }
`;

const StatusRow = styled.div`
  padding: 0.6rem 0.85rem;
  font-size: 0.82rem;
  color: #6b7280;
`;

export default function PlaceSearch({ vesselPosition, onResultSelected }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null); // null = nothing searched yet
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const debounceRef = useRef(null);
  const requestSeq = useRef(0);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 3) {
      setResults(null);
      setLoading(false);
      setError(null);
      return undefined;
    }
    debounceRef.current = setTimeout(async () => {
      const seq = ++requestSeq.current;
      setLoading(true);
      setError(null);
      try {
        const params = { q };
        if (vesselPosition) {
          params.lat = vesselPosition.latitude;
          params.lon = vesselPosition.longitude;
        }
        const resp = await api.get('/api/geo/search', { params });
        if (seq !== requestSeq.current) return; // stale response
        setResults(Array.isArray(resp.data) ? resp.data : []);
      } catch (e) {
        if (seq !== requestSeq.current) return;
        setError(e.response?.data?.error || `Search failed (${e.response?.status || e.message}) — try again`);
        setResults([]);
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    }, 500);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const pick = (r) => {
    setResults(null);
    setQuery('');
    onResultSelected(r);
  };

  return (
    <SearchWrap>
      <SearchBox>
        <MagnifyingGlassIcon />
        <input
          type="text"
          placeholder="Search place, marina, or lat, lon…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && results && results.length > 0) pick(results[0]);
            if (e.key === 'Escape') { setQuery(''); setResults(null); }
          }}
        />
        {query && (
          <button onClick={() => { setQuery(''); setResults(null); }} title="Clear search">
            <XMarkIcon />
          </button>
        )}
      </SearchBox>
      {(loading || error || (results && results.length >= 0 && query.trim().length >= 3)) && (
        <Results>
          {loading && <StatusRow>Searching…</StatusRow>}
          {!loading && error && <StatusRow>{error}</StatusRow>}
          {!loading && !error && results && results.length === 0 && <StatusRow>No results found</StatusRow>}
          {!loading && results && results.map((r, i) => (
            <ResultItem key={i} onClick={() => pick(r)}>
              {r.name}
              <div className="type">
                {(r.type || '')
                  .replace('marine-business', 'marine business')
                  .replace('marine-', '')
                  .replace(/^fuel$/, 'fuel dock')}
                {r.rating != null && ` · ★ ${r.rating.toFixed(1)}`}
                {r.distanceNm != null &&
                  ` · ${r.distanceNm < 1 ? `${Math.round(r.distanceNm * 1852)} m` : `${r.distanceNm.toFixed(1)} nm`} from your boat`}
                {r.address ? ` · ${r.address}` : ''}
              </div>
            </ResultItem>
          ))}
        </Results>
      )}
    </SearchWrap>
  );
}
